/** @jsxImportSource ../../i18n */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cx } from '../../theme/cx';
import { Button, IconButton } from './Button';

export const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const trapFocus = (event, container) => {
  const nodes = [...container.querySelectorAll(FOCUSABLE)].filter((el) => !el.hasAttribute('data-focus-skip'));
  if (nodes.length === 0) {
    event.preventDefault();
    return;
  }
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const SIZES = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' };

export const Dialog = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  position = 'center',
  initialFocusRef,
  showClose = true,
  hideHeader = false,
  ariaLabel,
  className,
  panelClassName,
}) => {
  const panel = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const node = panel.current;
    (initialFocusRef?.current || node?.querySelector(FOCUSABLE) || node)?.focus?.();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current?.();
      } else if (event.key === 'Tab' && node) {
        trapFocus(event, node);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, [open, initialFocusRef]);

  if (!open) return null;
  return createPortal(
    <div className={cx('fixed inset-0 z-dialog flex justify-center px-4', position === 'top' ? 'items-start pt-[12vh]' : 'items-center py-8', className)}>
      <div className="absolute inset-0 animate-fade-in bg-black/60 backdrop-blur-sm" aria-hidden="true" onMouseDown={() => onCloseRef.current?.()} />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title && !hideHeader ? titleId : undefined}
        aria-label={ariaLabel || (hideHeader && typeof title === 'string' ? title : undefined)}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cx('relative w-full animate-rise-in rounded-card border border-line bg-surface-1 shadow-pop focus:outline-none', SIZES[size] || SIZES.md, panelClassName)}
      >
        {!hideHeader && (title || showClose) && (
          <div className={cx('flex items-start justify-between gap-4 px-6 pt-6', !children && 'pb-6')}>
            <div className="min-w-0">
              {title && <h2 id={titleId} className="font-display text-xl text-fg">{title}</h2>}
              {description && <p id={descriptionId} className="mt-1.5 text-sm leading-relaxed text-fg-muted">{description}</p>}
            </div>
            {showClose && <IconButton icon={X} label="Fermer" size="sm" tooltip={false} onClick={() => onCloseRef.current?.()} className="-mr-2 -mt-1" />}
          </div>
        )}
        {children && <div className={cx(!hideHeader && 'px-6 pb-6 pt-4')}>{children}</div>}
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-line px-6 py-4">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

export const ConfirmDialog = ({ open, title, message, confirmLabel = 'Confirmer', cancelLabel = 'Annuler', tone = 'danger', onConfirm, onCancel }) => {
  const cancel = useRef(null);
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      description={message}
      size="sm"
      initialFocusRef={cancel}
      showClose={false}
      footer={(
        <>
          <Button ref={cancel} variant="secondary" onClick={onCancel}>{cancelLabel}</Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm}>{confirmLabel}</Button>
        </>
      )}
    />
  );
};

export const useConfirm = () => {
  const [request, setRequest] = useState(null);
  const confirm = useCallback((options) => new Promise((resolve) => setRequest({ ...options, resolve })), []);
  const settle = (result) => {
    request?.resolve(result);
    setRequest(null);
  };
  const dialog = (
    <ConfirmDialog
      open={Boolean(request)}
      title={request?.title}
      message={request?.message}
      confirmLabel={request?.confirmLabel}
      cancelLabel={request?.cancelLabel}
      tone={request?.tone}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );
  return [confirm, dialog];
};
